import { useCallback, useEffect } from 'react';
import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AuthCancelledError } from '@/store/slices/auth-ui-slice';

type AuthFlowConfig = {
  title?: string;
  description?: string;
  // Force a particular step and lock the sheet (e.g., signup-details)
  forceStep?: 'email-input' | 'login-otp' | 'signup-otp' | 'signup-details' | 'verify-phone';
  locked?: boolean;
};

export function useAuthModal() {
  const begin = useStore(s => s.authUI.begin);
  const setLocked = useStore(s => s.authUI.setLocked);
  const setStep = useStore(s => s.authUI.setStep);
  const resolvePending = useStore(s => s.authUI.resolvePending);
  const rejectPending = useStore(s => s.authUI.rejectPending);
  const isOpen = useStore(s => s.authUI.isOpen);
  const locked = useStore(s => s.authUI.locked);
  const analytics = useAnalytics();

  const openAuthFlow = useCallback(
    (config?: AuthFlowConfig) => {
      // Centralized event: user attempted to open auth flow from anywhere in the app.
      // Fire only when we're actually transitioning from closed -> open.
      if (!isOpen) {
        analytics.track('login_signup_attempt');
      }
      if (config?.forceStep) {
        const shouldLock = config?.locked !== undefined ? config.locked : true;
        const p = begin({
          title: config?.title ?? '',
          description: config?.description ?? '',
          locked: shouldLock,
        });
        setStep(config.forceStep);
        setLocked(shouldLock);
        return p;
      }
      return begin({
        title: config?.title ?? '',
        description: config?.description ?? '',
        locked: !!config?.locked,
      });
    },
    [analytics, begin, isOpen, setLocked, setStep],
  );

  const resolveAuthFlow = useCallback(() => {
    resolvePending();
  }, [resolvePending]);

  const rejectAuthFlow = useCallback(
    (err: unknown) => {
      rejectPending(err);
    },
    [rejectPending],
  );

  useEffect(() => {
    if (!isOpen && !locked) {
      rejectPending(new AuthCancelledError());
    }
  }, [isOpen, locked, rejectPending]);

  return { openAuthFlow, resolveAuthFlow, rejectAuthFlow };
}
